import {
  Alert,
  Box,
  Button,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { useAlert } from "contexts/alertContext";
import { Feature } from "models/api";
import { IEmployeeWorkshopData, IEmployeeWorkshopInput } from "models/workshop";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { QueryClient, useMutation } from "react-query";

import {
  editEmployeeWorkshop,
  getEmployeeWorkshopDetails,
} from "framework/workshops";
import { handleError } from "helpers/utils";
import { useTranslation } from "react-i18next";
import { Link, useParams, useSearchParams } from "react-router-dom";
import BackButton from "components/UI/BackButton";
import { getUserData } from "helpers";
import AddRejection from "../partials/AddRejection";

import { useLocation } from "react-router-dom";
import { Assignment, Poll, TextSnippet } from "@mui/icons-material";
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import LocalLibraryIcon from '@mui/icons-material/LocalLibrary';
import WorkshopFeatureCard from "./partials/WorkshopFeatureCard";
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import SelectedFeature from "./partials/SelectedFeature";
import { IWorkshopFeature } from "models/EmployeeWorkshopAssessment";
import { deleteWaitingList, withdraw } from "framework/enrollment";

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

interface Props {
  feature: Feature;
}

const EmployeeWorkshopDetails: React.FC<Props> = ({ feature }) => {
  const urlParams = useParams();
  const query = useQuery();
  const invitationId = query.get("invitationId");
  let id: string = urlParams[`${feature.singleName}Id`] as string;
  const userData = getUserData();
  const { colors } = userData;
  const [searchParams] = useSearchParams();
  const tab = searchParams.get("tab");
  const [loading, setLoading] = useState<boolean>(true);
  const [loading2, setLoading2] = useState<boolean>(false);
  const [openReject, setOpenReject] = useState<boolean>(false);

  const [inputObj, setInputObj] = useState<IEmployeeWorkshopInput>(
    {} as IEmployeeWorkshopInput
  );

  const [isError, setIsError] = useState<boolean>(false);
  const { t, i18n } = useTranslation();
  const [Workshop, setWorkshop] = useState<IEmployeeWorkshopData>(
    {} as IEmployeeWorkshopData
  );
  const [selectedFeature, setSelectedFeature] = useState<'tests' | 'evaluation' | 'polls' | 'materials' | 'attendance' | 'certificates' | null>(null);
  const selectedFeatureRef = useRef<HTMLDivElement>(null);

  const handleChange = (name: string, value: any) => {
    setInputObj((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const { setAlert } = useAlert();

  // eslint-disable-next-line
  const queryClient = new QueryClient();

  const invitationWorkshopMutation = useMutation({
    mutationFn: (data: { createInput: IEmployeeWorkshopInput }) => {
      return editEmployeeWorkshop(data.createInput, invitationId!);
    },
  });

  const deleteWaitingListMutation = useMutation({
      mutationFn: (waitingListId: string) => {
        return deleteWaitingList(
          waitingListId
        );
      },
    });

    const withdrawMutation = useMutation({
      mutationFn: (workshopId: string) => {
        return withdraw(
          workshopId
        );
      },
    });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let response: any = await queryClient.fetchQuery(
        "get Workshop Details",
        () => getEmployeeWorkshopDetails("/" + id)
      );
      setWorkshop(response.workshopDetails[0]);
      setLoading(false);
    } catch (error: any) {
      setIsError(true);
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, []);

  if (loading)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );
  if (isError)
    return (
      <Alert severity="error">
        Something went wrong, please try again later.
      </Alert>
    );

  const handleCloseReject = () => {
    setOpenReject(false);
    setInputObj({} as IEmployeeWorkshopInput);
  };

  const handleSendInvitations = async (status: number) => {
    try {
      setLoading2(true);
      await invitationWorkshopMutation.mutateAsync({
        createInput: {
          id: invitationId!,
          rejectionReason: inputObj.rejectionReason,
          status,
          workshopId: id,
        },
      });
      setAlert({
        open: true,
        message: "Workshop invitations sent successfully",
        type: "success",
      });
      fetchData();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleWithdraw = async (workshopId: string) => {
      try {
        setLoading2(true);
        const res = await withdrawMutation.mutateAsync(workshopId);
        setAlert({
          open: true,
          message: handleError(res),
          type: "success",
        });
        fetchData();
        setLoading2(false);
      } catch (error: any) {
        setAlert({
          open: true,
          message: handleError(error) || "Something went wrong",
          type: "error",
        });
        setLoading2(false);
      }
    }

  const handleDeleteWaitingList = async (waitingListId: string) => {
    try {
      setLoading2(true);
      const res = await deleteWaitingListMutation.mutateAsync(waitingListId);
      setAlert({
        open: true,
        message: handleError(res),
        type: "success",
      });
      fetchData();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  }


  let topics: IWorkshopFeature[] = [{
    title: "T_tests",
    slug: "tests",
    redirectUrl: "tests",
    icon: <TextSnippet sx={{ fontSize: 40, color: 'white' }} />,
    icon2: <TextSnippet sx={{ fontSize: 40, color: '#676af1' }} />,
    color1: '#9c27b0',
    color2: '#676af1'
  },
  // {
  //   title: "T_attendance",
  //   slug: "attendance",
  //   redirectUrl: "attendance",
  //   icon: <FormatListNumberedIcon sx={{ fontSize: 40, color: 'white' }} />,
  //   icon2: <FormatListNumberedIcon sx={{ fontSize: 40, color: '#3d5afe' }} />,
  //   color1: '#9c27b0',
  //   color2: '#3d5afe'
  // },
  {
    title: "T_certificates",
    slug: "certificates",
    redirectUrl: "certificates",
    icon: <CardMembershipIcon sx={{ fontSize: 40, color: 'white' }} />,
    icon2: <CardMembershipIcon sx={{ fontSize: 40, color: '#304ffe' }} />,
    color1: '#9c27b0',
    color2: '#304ffe'
  },
  {
    title: "T_evaluation",
    slug: "evaluation",
    redirectUrl: "evaluation",
    icon: <Assignment sx={{ fontSize: 40, color: 'white' }} />,
    icon2: <Assignment sx={{ fontSize: 40, color: '#1e88e5' }} />,
    color1: '#9c27b0',
    color2: '#1e88e5'
  },
  {
    title: "T_polls",
    slug: "polls",
    redirectUrl: "polls",
    icon: <Poll sx={{ fontSize: 40, color: 'white' }} />,
    icon2: <Poll sx={{ fontSize: 40, color: '#1976d2' }} />,
    color1: '#9c27b0',
    color2: '#1976d2'
  },
  {
    title: "T_material",
    slug: "materials",
    redirectUrl: "material",
    icon: <LocalLibraryIcon sx={{ fontSize: 40, color: 'white' }} />,
    icon2: <LocalLibraryIcon sx={{ fontSize: 40, color: '#1565c0' }} />,
    color1: '#9c27b0',
    color2: '#1565c0'
  }
  ];

  const handleSelectFeature = (feature: 'tests' | 'evaluation' | 'polls' | 'materials' | 'attendance' | 'certificates') => {
    if (selectedFeature === feature) {
      setSelectedFeature(null);
    } else {
      setSelectedFeature(feature);
      // Scroll to the selected feature section after state update
      setTimeout(() => {
        selectedFeatureRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }, 100);
    }
  }

  return (
    <>
      <div>
        <BackButton path="/workshops" text={t("T_back_to_workshops")} />
      </div>
      <Stack spacing={2} p={2}>
        <Grid
          container
          justifyContent={"space-between"}
          alignItems="center"
          sx={{ mb: 4 }}
        >
          <Typography
            variant="h3"
            color={colors?.labelColor ? colors?.labelColor : "header.primary"}
            style={{ paddingBlock: "30px" }}
          >
            {t("T_workshop_details")}
          </Typography>
        </Grid>
        <Grid
          container
          spacing={1}
        >
          {topics.map((topic: any, index) => (
            <Grid item key={index} xs={12} sm={6} md={3} lg={2}>
              <WorkshopFeatureCard
                title={topic.title}
                slug={topic.slug}
                redirectUrl={topic.redirectUrl}
                icon={topic.icon}
                color1={topic.color1}
                color2={topic.color2}

                onSelect={handleSelectFeature}
                isSelected={selectedFeature === topic.slug}
              />
            </Grid>
          ))}
        </Grid>
      </Stack>
      {!selectedFeature && <Box
        sx={{
          padding: "24px",
          backgroundColor: "#fff",
          borderRadius: "12px",
          boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.1)",
          height: "100%",
        }}
      >
        <Typography variant="h4" sx={{ marginBottom: "16px" }}>
          {i18n.language === "en" ? Workshop.topicName : Workshop.topicNameAr}
        </Typography>

        <Box sx={{ margin: "16px 0" }}>

          <Grid container margin={'20px 0'} >
            {Workshop.learningOutcome && (
              <Grid item xs={12}>
                <Typography variant="body1" sx={{ color: "#555", fontWeight: 500 }}>
                  {t('T_learning_outcome')}:
                </Typography>
                <Typography variant="body2" sx={{ color: "#888" }}>
                  {i18n.language === 'en' ? Workshop.learningOutcome : Workshop.learningOutcomeAr}
                </Typography>
              </Grid>
            )}
            {Workshop.companyName && (
              <Grid item xs={12} sm={6}>
                <Typography variant="body1" sx={{ color: "#555", fontWeight: 500 }}>
                  {t('T_company')}:
                </Typography>
                <Typography variant="body2" sx={{ color: "#888" }}>
                  {i18n.language === 'en' ? Workshop.companyName : Workshop.companyNameAr}
                </Typography>
              </Grid>
            )}


            <Grid item xs={12} sm={6}>
              <Typography variant="body1" sx={{ color: "#555", fontWeight: 500 }}>
                {t('T_date')}:
              </Typography>
              <Typography variant="body2" sx={{ color: "#888" }}>
                {Workshop.fromDate} - {Workshop.toDate}
              </Typography>
            </Grid>

            {Workshop.fromTime && Workshop.toTime && (
              <Grid item xs={12} sm={6}>
                <Typography variant="body1" sx={{ color: "#555", fontWeight: 500 }}>
                  {t('T_time')}:
                </Typography>
                <Typography variant="body2" sx={{ color: "#888" }}>
                  {Workshop.fromTime} - {Workshop.toTime}
                </Typography>
              </Grid>
            )}

            {Workshop.vendorName && (
              <Grid item xs={12} sm={6}>
                <Typography variant="body1" sx={{ color: "#555", fontWeight: 500 }}>
                  {t('T_vendors')}:
                </Typography>
                <Typography variant="body2" sx={{ color: "#888" }}>
                  {i18n.language === 'en' ? Workshop.vendorName : Workshop.vendorNameAr}
                </Typography>
              </Grid>
            )}

            {Workshop.venueName && (
              <Grid item xs={12} sm={6}>
                <Typography variant="body1" sx={{ color: "#555", fontWeight: 500 }}>
                  {t('T_venus')}:
                </Typography>
                <Typography variant="body2" sx={{ color: "#888" }}>
                  {i18n.language === 'en' ? Workshop.venueName : Workshop.venueNameAr}
                </Typography>
              </Grid>
            )}

            {Workshop.roomNumber && (
              <Grid item xs={12} sm={6}>
                <Typography variant="body1" sx={{ color: "#555", fontWeight: 500 }}>
                  {t('T_room_number')}:
                </Typography>
                <Typography variant="body2" sx={{ color: "#888" }}>
                  {Workshop.roomNumber}
                </Typography>
              </Grid>
            )}

            {Workshop.capacity && (
              <Grid item xs={12} sm={6}>
                <Typography variant="body1" sx={{ color: "#555", fontWeight: 500 }}>
                  {t('T_capacity')}:
                </Typography>
                <Typography variant="body2" sx={{ color: "#888" }}>
                  {Workshop.capacity}
                </Typography>
              </Grid>
            )}
            {Workshop.topicName && (
              <Grid item xs={12} sm={6}>
                <Typography variant="body1" sx={{ color: "#555", fontWeight: 500 }}>
                  {t('T_topics')}:
                </Typography>
                <Typography variant="body2" sx={{ color: "#888" }}>
                  {i18n.language === 'en' ? Workshop.topicName : Workshop.topicNameAr}
                </Typography>
              </Grid>
            )}

            {Workshop.trainer && (
              <Grid item xs={12} sm={6}>
                <Typography variant="body1" sx={{ color: "#555", fontWeight: 500 }}>
                  {t('T_trainer_name')}:
                </Typography>
                <Typography variant="body2" sx={{ color: "#888" }}>
                  {i18n.language === 'en' ? Workshop.trainer : Workshop.trainerAr}
                </Typography>
              </Grid>
            )}
            {/* {Workshop.roomNumber && (
              <Grid item xs={12} sm={6}>
                <Typography variant="body1" sx={{ color: "#555", fontWeight: 500 }}>
                  {t('T_room_number')}:
                </Typography>
                <Typography variant="body2" sx={{ color: "#888" }}>
                  {Workshop.roomNumber}
                </Typography>
              </Grid>
            )} */}

            {Workshop.trainerBiography && (
              <Grid item xs={12}>
                <Typography variant="body1" sx={{ color: "#555", fontWeight: 500 }}>
                  {t('T_trainer_bio')}:
                </Typography>
                <Typography variant="body2" sx={{ color: "#888" }}>
                  {i18n.language === 'en' ? Workshop.trainerBiography : Workshop.trainerBiographyAr}
                </Typography>
              </Grid>
            )}
          </Grid>

        </Box>

        <Box sx={{ display: "flex", justifyContent: "center", gap: "10px" }}>
          {Workshop.canEnroll && (
            <Button
              variant="contained"
              color="primary"
              sx={{ textTransform: "none", borderRadius: 0 }}
              onClick={() => handleSendInvitations(1)}
            >
              {t("T_enroll")}
            </Button>
          )}
          {/* {Workshop.canReject && (
            <Button
              variant="outlined"
              color="secondary"
              sx={{ textTransform: "none", borderRadius: 0 }}
              onClick={() => setOpenReject(true)}
            >
              {t("T_reject")}
            </Button>
          )}
          {Workshop.canWithdraw && (
            <Button
                variant="contained"
                color="error"
                sx={{ textTransform: "none", borderRadius: 0 }}
                onClick={() => tab == 'enrolledWorkshops' ? handleWithdraw(Workshop.workshopId) : handleDeleteWaitingList(Workshop.workshopId) }
              >
                {t("T_withdraw")}
              </Button>
            )} */}
          {/* {Workshop.showWokrshopAssessment && (
            <Link to={`/employeeWorkshopEvaluation/${id}`}>
              <Button
                variant="outlined"
                color="secondary"
                sx={{ textTransform: "none", borderRadius: 0 }}
                onClick={() => setOpenReject(true)}
              >
                {t("T_evaluation")}
              </Button>
            </Link>
          )} */}
        </Box>
      </Box>}
      {selectedFeature && (
        <div ref={selectedFeatureRef}>
          <SelectedFeature
            workshopId={Workshop.workshopId}
            companyId={Workshop.companyId}
            enrollmentId={Workshop.enrollmentId}
            eligibleForCertificate={Boolean(
              Workshop?.eligibleForCertificate ??
              Workshop?.enrollmentStatus?.eligibleForCertificate
            )}
            workshopTitle={i18n.language === "en" ? Workshop.topicName : Workshop.topicNameAr}
            feature={topics.find((topic) => topic.slug === selectedFeature)!}
            onClose={() => setSelectedFeature(null)}
          />
        </div>
      )}
      {openReject && (
        <AddRejection
          onChanges={handleChange}
          onClose={handleCloseReject}
          open={openReject}
          loading={loading2}
          onSubmit={() => handleSendInvitations(2)}
        />
      )}
    </>
  );
};

export default EmployeeWorkshopDetails;
